import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CalculatorTeaser = () => {
  const indicators = [
    { icon: 'ri-moon-line', label: 'Disrupted Sleep Patterns' },
    { icon: 'ri-group-line', label: 'Others Are Now Involved' },
    { icon: 'ri-file-list-3-line', label: 'You Have a Dedicated Folder' },
    { icon: 'ri-refresh-line', label: 'Prior Solutions Made It Worse' },
  ];

  return (
    <section className="relative py-32 bg-black overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/fc57d4ff5a12d5a8b39de9dd4817e441.jpg"
          alt="Storm clouds"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-black/90"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
              Free Diagnostic Tool
            </span>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight font-serif">
              How Alarming Is Your Situation, Really?
            </h2>
            <p className="text-xl text-cream/75 mb-8 leading-relaxed">
              Our proprietary Problem Severity Calculator™ uses a 14-framework diagnostic matrix to measure the true depth, complexity, and untapped catastrophic potential of your current circumstances. Results are guaranteed to be concerning.
            </p>
            <p className="text-cream/50 text-base mb-10 italic">
              "I came in thinking I had one problem. The calculator identified nine. Three of them were new." — Margaret T., Former Optimist
            </p>

            <Link
              to="/calculator"
              className="inline-flex items-center gap-3 bg-gold text-black px-10 py-5 rounded-full text-lg font-black hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-stethoscope-line text-xl"></i>
              Calculate My Severity
            </Link>
          </motion.div>

          {/* Right: Visual Card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-dark-gray/70 backdrop-blur-sm border border-gold/20 rounded-3xl p-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-cream/50 text-xs uppercase tracking-widest mb-1">Sample Result</p>
                  <p className="text-white font-black text-lg">Problem Severity Score</p>
                </div>
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-500/20 border border-red-500/40">
                  <i className="ri-alarm-warning-fill text-2xl text-red-400"></i>
                </div>
              </div>

              {/* Fake Score Bar */}
              <div className="mb-8">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-7xl font-black text-white leading-none">87</span>
                  <span className="text-red-400 font-black text-sm uppercase tracking-widest mb-2">Severe</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <motion.div
                    className="h-3 rounded-full bg-gradient-to-r from-amber-500 to-red-500"
                    initial={{ width: 0 }}
                    whileInView={{ width: '87%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, delay: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <div className="flex justify-between mt-1.5 text-xs text-cream/30">
                  <span>Suspiciously Fine</span>
                  <span>Gloriously Catastrophic</span>
                </div>
              </div>

              {/* Indicators */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {indicators.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3"
                  >
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-gold text-base`}></i>
                    </div>
                    <span className="text-cream/70 text-xs font-medium leading-tight">{item.label}</span>
                  </motion.div>
                ))}
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                <p className="text-red-300 text-xs font-semibold uppercase tracking-wide mb-1">
                  Official Diagnostic Notice
                </p>
                <p className="text-cream/60 text-xs leading-relaxed">
                  This score qualifies for our Gold Problem Tier. A Problem Awareness Specialist will be in touch. Please do not attempt to resolve anything in the meantime.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default CalculatorTeaser;
